import { useParams } from 'react-router-dom';
import { useUserPage } from './useUserPage';
import UserDetails from '../common/UserDetails/UserDetails';
import {
    Button,
    GenericError,
    PageLoader,
    Table,
    Tabs,
    TextField,
    Title,
} from '../common';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import EventRowSmall from '../common/EventRowSmall/EventRowSmall';
import { useMemo, useState } from 'react';
import { FollowersDialog } from './FollowersDialog/FollowersDialog';

import './userPage.css';

const UserPage = () => {
    const { userId } = useParams();
    const {
        isLoading,
        userDetails,
        userHistory,
        error,
        showLoadMoreButton,
        onShowMoreClick,
        isMoreLoading,
        followUser,
        fetchUserFollowers,
        userFollowers,
        isUserFollowersLoading,
        followUserFromDialog,
        userReports,
        userStats,
    } = useUserPage(userId);
    const { t } = useTranslation();
    const [isFolloerDialogOpen, setIsFollowerDialogOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(t('userPage.firstTab'));

    const sections = useMemo(
        () => [t('userPage.firstTab'), t('userPage.secondTab')],
        [userId]
    );

    const onChangeTab = (tab: string) => {
        setSelectedTab(tab);
    };

    if (isLoading) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    if (!userDetails || !userHistory) {
        return null;
    }

    const { eventHistory, nextEvents } = userHistory;

    function onFollowersClick() {
        setIsFollowerDialogOpen(true);
        fetchUserFollowers();
    }

    return (
        <div className="user-page page-container">
            <UserDetails
                user={userDetails}
                onFollowUser={followUser}
                onFollowersClick={onFollowersClick}
                userReports={userReports}
            />
            <div style={{ marginTop: '50px' }}>
                <Tabs
                    sections={sections}
                    selectedTab={selectedTab}
                    onChangeTab={onChangeTab}
                />
            </div>
            {selectedTab === sections[1] && (
                <div className="user-page-stats">
                    {userStats && userStats?.length > 0 ? (
                        <div>
                            <Table
                                title={t('userPage.tableTitle')}
                                columns={[
                                    {
                                        key: 'sportEventType',
                                        label: t('userPage.sport'),
                                    },
                                    {
                                        key: 'eventCount',
                                        label: t('userPage.eventCount'),
                                    },
                                    {
                                        key: 'averageRating',
                                        label: t('userPage.averageRating'),
                                    },
                                ]}
                                tableData={userStats ?? []}
                                renderers={{
                                    sportEventType: {
                                        iconName: (_value, row) => {
                                            return `bx-${(
                                                row.sportEventType as string
                                            ).toLowerCase()}`;
                                        },
                                        render: value => (
                                            <span>
                                                {t(
                                                    `eventTypes.${value.toLowerCase()}`
                                                )}
                                            </span>
                                        ),
                                    },
                                    averageRating: {
                                        render: value =>
                                            Number(value).toFixed(1),
                                    },
                                    eventCount: {
                                        render: value => (
                                            <>
                                                <div className="count-label">
                                                    {value}
                                                </div>
                                            </>
                                        ),
                                    },
                                }}
                            />
                        </div>
                    ) : (
                        <TextField>{t('userPage.noRatings')}</TextField>
                    )}
                </div>
            )}
            {selectedTab === sections[0] && (
                <div className="user-page-events">
                    <div className="user-page-next-events">
                        <Title text={t('userDetails.nextEvents')} size="xl" />
                        <div className="user-history-list">
                            {nextEvents.length > 0 ? (
                                nextEvents.map(record => {
                                    return (
                                        <EventRowSmall
                                            key={record.id}
                                            eventRecord={record}
                                            isMvp={record.mvp === userId}
                                            isUserPage
                                            isListView
                                        />
                                    );
                                })
                            ) : (
                                <div className="dashboard-empty-list">
                                    <TextField>
                                        {t('userDetails.noEventsScheduled')}
                                    </TextField>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="user-page-history">
                        <Title text={t('userDetails.lastEvents')} size="xl" />
                        <div className="user-history-list">
                            {eventHistory.length > 0 ? (
                                eventHistory.map(record => {
                                    return (
                                        <EventRowSmall
                                            key={record.id}
                                            eventRecord={record}
                                            isWinner={false}
                                            isMvp={record.mvp === userId}
                                            isUserPage
                                        />
                                    );
                                })
                            ) : (
                                <div className="dashboard-empty-list">
                                    <TextField>
                                        {t('userPage.noEventHistory')}
                                    </TextField>
                                </div>
                            )}
                        </div>
                        {showLoadMoreButton && (
                            <div className="event-list-btn">
                                <Button
                                    text="Show more records"
                                    variant="green"
                                    size="md"
                                    onClick={onShowMoreClick}
                                    disabled={isMoreLoading}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FollowersDialog
                isOpen={isFolloerDialogOpen}
                onClose={() => setIsFollowerDialogOpen(false)}
                followers={userFollowers}
                isLoading={isUserFollowersLoading}
                followUser={followUserFromDialog}
            ></FollowersDialog>
        </div>
    );
};

export default UserPage;
